import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Grid3X3, Info, ChevronDown, ChevronUp, Crosshair, Landmark, AlertTriangle } from "lucide-react";

const BASIS_STYLE = {
  "Factual evidence": { tag: "F", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "Regulatory fact": { tag: "R", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "HCP perception": { tag: "P", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  "AI-generated interpretation": { tag: "AI", cls: "bg-violet-100 text-violet-800 border-violet-300 border-dashed" },
  "Expert-reviewed conclusion": { tag: "E", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  "Analyst scenario": { tag: "S", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  "Insufficient evidence": { tag: "—", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const PRESSURE_CLS = {
  "Material near-term pressure": "bg-red-50 text-red-700 border-red-200",
  "Material longer-term pressure": "bg-amber-50 text-amber-700 border-amber-200",
  "Emerging competitor": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Monitor: "bg-slate-50 text-slate-600 border-slate-200",
  "Insufficient evidence": "bg-slate-100 text-slate-500 border-slate-300",
};
const TABS = [["summary", "Competitive Summary"], ["matrix", "Comparison Matrix"], ["profiles", "Competitor Profiles"]];

export default function CompetitiveIntel() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("summary");
  const [selected, setSelected] = useState(["camizestrant", "imlunestrant"]);
  const [openGroups, setOpenGroups] = useState(["A. Product & regulatory profile", "C. HCP voice"]);
  const [cell, setCell] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => { api.get("/competitive-matrix").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading competitive intelligence…</div>;

  const anchor = data.assets.find((a) => a.is_anchor);
  const comparators = data.assets.filter((a) => !a.is_anchor);
  const columns = [anchor, ...comparators.filter((c) => selected.includes(c.id))];
  const syn = data.synthesis;

  const toggleComparator = (id) => setSelected((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= 3) return s;
    return [...s, id];
  });

  return (
    <div className="space-y-4" data-testid="competitive-view">
      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`comp-tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>{label}</button>
        ))}
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-400">As of {data.as_of} · evidence cut-off {data.evidence_cutoff} · Illustrative demonstration data</span>
      </div>

      {tab === "summary" && (
        <div className="space-y-4" data-testid="comp-summary">
          <div className="bg-slate-900 rounded-xl p-5 border border-slate-800" data-testid="comp-synthesis">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-teal-400">Competitive intelligence synthesis</p>
              <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/30">{syn.status}</Badge>
              <span className="text-[10px] text-slate-500 ml-auto">Confidence: {syn.confidence} · Last reviewed {syn.last_reviewed}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
              {[["Current position", syn.position], ["Main pressure source", syn.pressure], ["Principal opportunity", syn.opportunity], ["Major uncertainty", syn.uncertainty]].map(([l, v]) => (
                <div key={l} className="flex items-start gap-3">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 bg-teal-500/10 border border-teal-500/25 rounded px-1.5 py-0.5 mt-0.5 shrink-0 whitespace-nowrap">{l}</span>
                  <p className="text-xs text-slate-200 leading-snug">{v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="reference-profile">
              <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-2 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" />Elacestrant reference profile — comparisons are made against this defined profile</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {Object.entries(data.reference).map(([l, v]) => (
                  <div key={l}><p className="text-[9px] font-mono uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px] text-slate-700">{v}</p></div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-teal-600" />Top competitive pressures</p>
              {data.competitors.slice(0, 3).map((c) => (
                <button key={c.id} onClick={() => { setTab("profiles"); setProfile(c.id); }} className="w-full text-left border border-slate-100 rounded-lg p-2.5 mb-2 hover:border-teal-300 transition-colors" data-testid={`summary-pressure-${c.id}`}>
                  <p className="text-xs font-bold text-slate-900 flex items-center gap-2">{c.name}
                    <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 ${PRESSURE_CLS[c.pressure_class]}`}>{c.pressure_class}</span></p>
                  <p className="text-[10.5px] text-slate-500 mt-0.5">{c.phase} · {c.reg_status}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="comp-scenarios">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Strategic scenarios — scenarios, not forecasts</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2">
              {data.scenarios.map((sc) => (
                <div key={sc.name} className="border border-slate-200/80 rounded-lg p-3">
                  <p className="text-xs font-bold text-slate-900">{sc.name} <span className="text-[9px] font-mono uppercase text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5 ml-1">Scenario — not confirmed</span></p>
                  <p className="text-[10.5px] text-slate-600 mt-1"><span className="text-slate-400">Trigger:</span> {sc.trigger}</p>
                  <p className="text-[10.5px] text-slate-600"><span className="text-slate-400">Implication:</span> {sc.implication}</p>
                  <p className="text-[10px] text-slate-500 mt-1">Assets: {sc.assets} · Uncertainty: {sc.uncertainty}</p>
                  <p className="text-[10px] text-teal-700 mt-0.5">Monitoring: {sc.action}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "matrix" && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="competitive-matrix">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-4 h-4 text-teal-600" />
              <h3 className="font-heading text-base font-bold text-slate-900">Comparison matrix — elacestrant reference vs up to 3 comparators</h3>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="comparator-selector">
              {comparators.map((c) => (
                <button key={c.id} data-testid={`comparator-toggle-${c.id}`} onClick={() => toggleComparator(c.id)}
                  className={`text-xs font-semibold rounded-full border px-3 py-1 transition-colors ${selected.includes(c.id) ? "bg-slate-900 text-teal-300 border-slate-900" : "bg-slate-50 text-slate-500 border-slate-200 hover:border-teal-400"} ${!selected.includes(c.id) && selected.length >= 3 ? "opacity-40" : ""}`}>
                  {c.name.split(" ")[0]}</button>
              ))}
            </div>
          </div>
          <p className="text-[10.5px] text-slate-500 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2 mb-3 flex items-start gap-1.5" data-testid="comparability-notice">
            <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            Comparisons are indirect unless an appropriate head-to-head study is available. Differences in trial population, treatment line, biomarker selection, comparator, endpoint, follow-up and development status limit cross-study interpretation. Where contexts differ, cells state "Context differs" rather than implying a favourable or unfavourable result.
          </p>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="basis-legend">
            {Object.entries(BASIS_STYLE).map(([label, st]) => (
              <span key={label} className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <span className={`font-mono font-bold text-[9px] rounded border px-1 ${st.cls}`}>{st.tag}</span>{label}</span>
            ))}
          </div>
          <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="sticky top-0 bg-white z-10">
                  <th className="font-mono text-[9px] uppercase tracking-widest text-slate-400 py-2 pr-3 w-44 sticky left-0 bg-white">Dimension</th>
                  {columns.map((a) => (
                    <th key={a.id} className="py-2 px-3 min-w-[210px]">
                      <p className={`text-xs font-bold ${a.is_anchor ? "text-teal-700" : "text-slate-900"}`}>{a.name}{a.is_anchor && <span className="ml-1.5 text-[8.5px] font-mono uppercase text-teal-600 border border-teal-200 rounded-full px-1.5 py-0.5">Reference</span>}</p>
                      <p className="text-[10px] text-slate-400 font-normal">{a.company}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.groups.map(([group, dims]) => (
                  <>
                    <tr key={group} className="border-t border-slate-200 bg-slate-50/70 cursor-pointer" onClick={() => setOpenGroups((g) => g.includes(group) ? g.filter((x) => x !== group) : [...g, group])} data-testid={`matrix-group-${group[0]}`}>
                      <td colSpan={columns.length + 1} className="py-2 pr-3 sticky left-0 bg-slate-50/70">
                        <span className="text-[10.5px] font-bold text-slate-700 flex items-center gap-1.5">{openGroups.includes(group) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}{group}</span>
                      </td>
                    </tr>
                    {openGroups.includes(group) && dims.map((dim) => (
                      <tr key={dim} className="border-t border-slate-100 align-top">
                        <td className="py-2.5 pr-3 text-[10.5px] font-semibold text-slate-500 sticky left-0 bg-white">{dim}</td>
                        {columns.map((a) => {
                          const c = a.cells[dim];
                          const st = BASIS_STYLE[c?.basis] || BASIS_STYLE["Insufficient evidence"];
                          return (
                            <td key={a.id} className="py-2.5 px-3">
                              {c ? (
                                <button className="text-left group" onClick={() => setCell({ asset: a, dim, cell: c })} data-testid={`matrix-cell-${a.id}-${dim.replace(/\W+/g, "-")}`}>
                                  <span className={`font-mono font-bold text-[9px] rounded border px-1 mr-1.5 ${st.cls}`}>{st.tag}</span>
                                  <span className="text-[11px] text-slate-700 group-hover:text-teal-700 leading-snug">{c.value}</span>
                                </button>
                              ) : <span className="text-[11px] text-slate-400">Context differs / insufficient evidence</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "profiles" && (
        <div className="space-y-3" data-testid="comp-profiles">
          {data.competitors.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200/80 rounded-xl shadow-sm" data-testid={`competitor-card-${c.id}`}>
              <button className="w-full text-left p-4" onClick={() => setProfile(profile === c.id ? null : c.id)} data-testid={`competitor-expand-${c.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-slate-900">{c.name} <span className="text-xs font-normal text-slate-500">· {c.company}</span></p>
                  <Badge variant="outline" className={`text-[9px] font-semibold ${PRESSURE_CLS[c.pressure_class]}`} data-testid={`pressure-class-${c.id}`}>{c.pressure_class}</Badge>
                  <span className="text-[10px] text-slate-400 ml-auto">{c.phase} · As of {c.as_of}</span>
                  {profile === c.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
                <p className="text-[10.5px] text-slate-500 mt-1">{c.reg_status} · {c.mechanism}</p>
              </button>
              {profile === c.id && (
                <div className="px-4 pb-4 space-y-3" data-testid={`competitor-detail-${c.id}`}>
                  <p className="text-xs text-slate-600 leading-snug">{c.positioning_note} <span className="text-[9px] font-mono uppercase text-violet-600">AI-generated interpretation — pending expert review</span></p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-[11px] text-slate-600">
                    <span>Key trials: <span className="font-medium">{c.key_trials.join(", ")}</span></span>
                    <span>Anticipated window: <span className="font-medium">{c.impact_window} (scenario — not confirmed)</span></span>
                    <span>Linked HCP statements: <span className="font-medium">{c.insight_ids.length}</span></span>
                    <span>Verified sources: <span className="font-medium">Source verification pending — illustrative</span></span>
                  </div>
                  {c.class_detail?.is_class && (
                    <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid={`class-detail-${c.id}`}>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Regional competitor class</p>
                      {Object.entries({ "Identified assets": c.class_detail.identified_assets, Scope: c.class_detail.scope, "Shared characteristics": c.class_detail.shared, "Important differences": c.class_detail.differences, "Why class-level grouping": c.class_detail.grouping_rationale }).map(([l, v]) => (
                        <p key={l} className="text-[10.5px] text-slate-600 mb-0.5"><span className="text-slate-400">{l}:</span> {v}</p>
                      ))}
                    </div>
                  )}
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Competitive-pressure assessment — decomposed, no proprietary score</p>
                    <div className="space-y-1.5" data-testid={`pressure-components-${c.id}`}>
                      {c.pressure_components.map((pc) => (
                        <div key={pc.component} className="border border-slate-100 rounded-lg px-3 py-2">
                          <p className="text-[11px] text-slate-700"><span className="font-semibold">{pc.component}:</span> {pc.assessment}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Evidence: {pc.evidence} · {pc.review_status}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />Illustrative demonstration data — supporting findings link to HCP Voice, Evidence Landscape, Congress Intelligence and the Source Library; only approved findings contribute to expert-reviewed conclusions or formal reports.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="cell-drawer">
          {cell && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-base text-left">{cell.asset.name} — {cell.dim}</SheetTitle>
              </SheetHeader>
              <div className="space-y-3 mt-4 text-xs text-slate-700">
                <Badge variant="outline" className={`text-[10px] ${BASIS_STYLE[cell.cell.basis]?.cls || ""}`}>{cell.cell.basis}</Badge>
                <p className="border-l-2 border-teal-500 pl-3 leading-relaxed">{cell.cell.value}</p>
                {[["Source", cell.cell.source || "Illustrative demonstration data — source verification pending"],
                  ["Source date", cell.cell.date || `As of ${data.as_of}`],
                  ["Geography", cell.cell.geography || "Global (multinational evidence base)"],
                  ["Relevant population", cell.cell.population || "See asset reference profile"],
                  ["Limitations", cell.cell.limitations || "Indirect comparison; trial contexts differ — do not interpret as head-to-head evidence."],
                  ["Verification status", cell.cell.basis === "Factual evidence" ? "Citation-level verification — full review pending (prototype)" : "Not applicable until reviewed"],
                  ["Reviewer / review date", cell.cell.basis === "Expert-reviewed conclusion" ? "MarksMan Scientific Review · 2026-06-13" : "Pending scientific review"],
                  ["Confidence", cell.cell.confidence || "Moderate"]].map(([l, v]) => (
                  <div key={l}><p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px]">{v}</p></div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
